import React from 'react';
import { StyleSheet } from 'react-native';
import { Typography, Box } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import view from '../../asset/images/icons/views.svg';
import comment from '../../asset/images/icons/comments.svg';
import like from '../../asset/images/icons/loves.svg';
import { EsPlayButton } from '../EsButtons/EsPlayButton';
import { formatNumberWithK } from '../../utils/AppUtils';
import { EsUserButtonPlus } from '../EsButtons/EsUserButtonPlus';
import { ISubmission, PartialUserSummary } from '../../redux/discovery/types';
import { IComment, IProject, IUserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

type Reaction = 'like' | 'share' | 'comment';
export interface VerticalCarousel {
    submission: ISubmission;
    comments?: IComment[];
    playlist?: IProject[];
    onClickPlay?: (projectId: ISubmission, index?: number, submission?: ISubmission) => void;
    onClickReaction?: (reactionType: Reaction, projectId: string) => void;
    onClickHandle?: (userId: PartialUserSummary) => void;
    index?: number;
}

const EsVerticalCard: React.FC<VerticalCarousel> = ({
    submission,
    onClickPlay,
    onClickReaction,
    onClickHandle,
    index,
}) => {
    const { themeStyle, device } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    return (
        <Box
            sx={{
                width: { xs: '45vw', sm: '284px' },
                minWidth: {
                    xs: '40vw',
                    sm: '275px',
                },
                height: {
                    xs: '300px',
                    sm: '458px',
                },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                borderRadius: '10px',
                border: 'none',
                backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2)))`, // Added gradient overlay here
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                overflow: 'hidden',
            }}
            key={submission._id}>
            <CustomLazyLoadImage
                effect="blur"
                src={submission.project.thumbnail ?? submission.project.feature_image}
                alt="background image"
                showGradient
                width={'100%'}
                height={'100%'}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    objectFit: 'cover',
                    height: device.isMobile ? '300px' : '458px',
                    zIndex: -1,
                }}
            />
            {/* Play button */}
            <EsPlayButton onClick={() => onClickPlay && onClickPlay(submission, index, submission)} />

            <Box sx={{ gap: '12px', marginBottom: '20px', zIndex: 2, maxWidth: '80%', alignSelf: 'center' }}>
                <Box style={styles.flexColumn} sx={{ gap: '6px' }}>
                    <Typography style={styles.textHandle}>{submission.project.title}</Typography>
                    <Typography
                        style={{
                            ...styles.share,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            ...(device.isMobile && {
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 1,
                            }),
                        }}>
                        {submission.project.description}
                    </Typography>
                </Box>

                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                    sx={{ width: '100%', height: '42px', gap: '20px', mt: 1 }}>
                    {/* User plus button */}
                    {submission && submission.created_by && submission.created_by.profile_picture ? (
                        <EsUserButtonPlus
                            image={`${submission.created_by.profile_picture}`}
                            onClickHandle={() =>
                                onClickHandle && onClickHandle(submission.created_by as PartialUserSummary)
                            }
                        />
                    ) : (
                        <ThspianAvatar
                            user={submission.created_by as unknown as IUserSummary}
                            onClick={() => onClickHandle && onClickHandle(submission.created_by)}
                            badgeSx={{
                                border: `1px solid ${theme[themeStyle].tag_white}`,
                                borderRadius: '50%',
                            }}
                        />
                    )}

                    <Box style={styles.flexRow}>
                        <Box
                            style={styles.shareBox}
                            sx={{
                                marginRight: {
                                    xs: 0.5,
                                    sm: 2,
                                },
                            }}
                            onClick={() => onClickReaction && onClickReaction('share', submission.project._id)}>
                            <CustomLazyLoadImage src={view} alt="view icon" width={device.isMobile ? 20 : 30} />
                            <Typography style={styles.share}>
                                {formatNumberWithK(submission.project.view_count)}
                            </Typography>
                        </Box>
                        <Box
                            style={styles.shareBox}
                            sx={{
                                marginRight: {
                                    xs: 0.5,
                                    sm: 2,
                                },
                            }}
                            onClick={() => onClickReaction && onClickReaction('comment', submission.project._id)}>
                            <CustomLazyLoadImage src={comment} alt="comment icon" width={device.isMobile ? 15 : 25} />
                            <Typography style={styles.share}>
                                {formatNumberWithK(submission.project.total_comments)}
                            </Typography>
                        </Box>
                        <Box
                            style={styles.shareBox}
                            onClick={() => onClickReaction && onClickReaction('like', submission.project._id)}>
                            <CustomLazyLoadImage src={like} alt="heart icon" width={device.isMobile ? 15 : 25} />
                            <Typography style={styles.share}>
                                {formatNumberWithK(submission.project.total_likes)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        shareBox: {
            width: 'fit-content',
            height: 'full',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        share: {
            fontSize: 11,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            textAlign: 'left',
            color: theme[colorScheme].true_white,
        },
        textHandle: {
            fontSize: 12,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].true_white,
        },
    });

export { EsVerticalCard };
